import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listDocs, getDoc } from '../services/docsService';
import { DocsSidebar } from '../components/docs/DocsSidebar';
import { DocsContent } from '../components/docs/DocsContent';

const SLUG_REGEX = /^[a-z0-9.-]+$/;

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

export const Documentation: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const autoExpandGroup = searchParams.get('group') || undefined;

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  // Auto-close drawer on slug change (operator clicked a link).
  useEffect(() => { setMobileSidebarOpen(false); }, [slug]);
  // Reset active anchor on doc change so the previous doc's anchor doesn't
  // briefly highlight in the new doc's sub-tier.
  useEffect(() => { setActiveAnchor(null); }, [slug]);

  const catalogQuery = useQuery(['docs-list'], listDocs, { staleTime: 5 * 60 * 1000 });

  if (catalogQuery.isLoading) {
    return <div className="px-8 py-6 text-gray-500">Loading documentation…</div>;
  }
  if (catalogQuery.isError || !catalogQuery.data) {
    return <div className="px-8 py-6 text-red-500">Failed to load docs catalog.</div>;
  }

  const catalog = catalogQuery.data;

  if (!slug) {
    return <Navigate to={`/docs/${catalog.default_slug}`} replace />;
  }

  // Slug regex validation guard — defense in depth even though backend 404s on bad slugs.
  if (!SLUG_REGEX.test(slug)) {
    return <Navigate to={`/docs/${catalog.default_slug}`} replace />;
  }

  const entry = catalog.docs.find((d) => d.slug === slug);
  if (!entry) {
    const suggestions = catalog.docs
      .map((d) => ({ slug: d.slug, title: d.title, dist: levenshtein(d.slug, slug) }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 3);
    return (
      <div className="flex">
        <DocsSidebar
          catalog={catalog}
          currentSlug=""
          activeAnchor={null}
          autoExpandGroup={autoExpandGroup}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />
        <div className="flex-1 px-8 py-6">
          <h1 className="text-2xl font-semibold mb-2">Doc not found</h1>
          <p className="text-gray-500 mb-3">
            No published doc with slug <code>{slug}</code>.
          </p>
          <p className="text-sm text-gray-400 mb-2">Did you mean:</p>
          <ul className="space-y-1">
            {suggestions.map((s) => (
              <li key={s.slug}>
                <a href={`/docs/${s.slug}`} className="text-emerald-400 hover:underline">
                  {s.title}
                </a>
                <span className="text-xs text-gray-500 ml-2"><code>{s.slug}</code></span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <DocsSidebar
        catalog={catalog}
        currentSlug={slug}
        activeAnchor={activeAnchor}
        autoExpandGroup={autoExpandGroup}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <DocContentWithFetch
        slug={slug}
        group={entry.group}
        hash={location.hash}
        onMobileToggle={() => setMobileSidebarOpen(true)}
        onActiveAnchorChange={setActiveAnchor}
      />
    </div>
  );
};

const DocContentWithFetch: React.FC<{
  slug: string;
  group: string;
  hash: string;
  onMobileToggle: () => void;
  onActiveAnchorChange: (anchor: string | null) => void;
}> = ({ slug, group, hash, onMobileToggle, onActiveAnchorChange }) => {
  const docQuery = useQuery(['docs', slug], () => getDoc(slug), { staleTime: 5 * 60 * 1000 });

  // Scroll to hash anchor after content renders.
  useEffect(() => {
    if (docQuery.isSuccess && hash) {
      const id = hash.slice(1);
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [docQuery.isSuccess, hash, slug]);

  // IntersectionObserver for active-heading detection. RAF wrap so the
  // observer attaches AFTER ReactMarkdown commits headings to the DOM
  // (mirrors the scroll-to-hash effect above).
  useEffect(() => {
    if (!docQuery.isSuccess) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    let observer: IntersectionObserver | null = null;
    const rafId = requestAnimationFrame(() => {
      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length > 0) {
            const id = (visible[0].target as HTMLElement).id;
            onActiveAnchorChange(id || null);
          }
        },
        { rootMargin: '0px 0px -80% 0px', threshold: 0 },
      );
      const headings = document.querySelectorAll(
        'main h2[id], main h3[id], main h4[id], main h5[id], main h6[id]',
      );
      headings.forEach((h) => observer!.observe(h));
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [docQuery.isSuccess, slug, onActiveAnchorChange]);

  if (docQuery.isLoading) {
    return <div className="flex-1 px-8 py-6 text-gray-500">Loading…</div>;
  }
  if (docQuery.isError || !docQuery.data) {
    return <div className="flex-1 px-8 py-6 text-red-500">Failed to load doc.</div>;
  }
  return <DocsContent doc={docQuery.data} group={group} onMobileToggle={onMobileToggle} />;
};
